import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  Lock,
  Users,
  Shield,
  Heart,
  MapPin,
  Briefcase,
  Search,
  Star,
  Home as HomeIcon,
} from 'lucide-react';
import SubpageShell from './SubpageShell';
import JsonLd, { SITE, PROVIDER } from '../components/JsonLd';
import SEO from '../components/SEO';
import { track } from '../lib/track';
import PageHeroImage from '../components/PageHeroImage';
import HomeSolutionsGate from '../access/HomeSolutionsGate';
import { useMember, hasTier, TIER_LABEL, formatApiError } from '../access/MemberAuthContext';
import '../styles/RainbowText.css';
import '../styles/Access.css';
import '../styles/AccessAuth.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* Five primary access areas — matches the mockup order & colours. */
const PRIMARY_AREAS = [
  {
    key: 'n3uro-access',
    image: '/img/access/n3uro-access.jpg',
    accent: 'purple',
    label: 'N3URO ACCESS',
    body: "Your family's access, profiles, passport, knowledge and tools — all in one secure place.",
    href: '#tiers',
  },
  {
    key: 'discovery',
    image: '/img/access/discovery.jpg',
    accent: 'lime',
    label: 'DISCOVERY',
    body: 'Find places and experiences that work better for your family.',
    href: '/access/discovery',
  },
  {
    key: 'journeys',
    image: '/img/access/journeys.jpg',
    accent: 'cyan',
    label: 'JOURNEYS',
    body: "Prepare, plan and save journeys for what's ahead with greater confidence.",
    href: '/access/journeys-guide',
  },
  {
    key: 'home-solutions',
    image: '/img/access/home-solutions.jpg',
    accent: 'orange',
    label: 'HOME SOLUTIONS',
    body: 'Explore solutions designed around real family life and your home spaces.',
    href: '#home-solutions',
  },
  {
    key: 'membership',
    image: '/img/access/membership.jpg',
    accent: 'pink',
    label: 'MEMBERSHIP',
    body: 'Choose the level of N3URO Access that unlocks the support your family needs.',
    href: '#tiers',
  },
];

/* Five mini value-props in the "What joining N3URO Access means" band. */
const VALUE_PROPS = [
  { icon: Shield, tone: 'purple', body: 'Practical tools and guidance for everyday life and journeys.' },
  { icon: Users,  tone: 'lime',   body: 'Solutions designed around neuro-inclusive family needs.' },
  { icon: Lock,   tone: 'cyan',   body: 'Your information is secure, private and always in your control.' },
  { icon: Heart,  tone: 'pink',   body: 'Save time, reduce stress and build confidence together.' },
  { icon: Users,  tone: 'orange', body: 'Join a community of families who understand.' },
];

/* Bottom value-prop band under the tiers — titled cards. */
const CLOSING_PROPS = [
  { icon: HomeIcon,   tone: 'purple', title: 'BUILT AROUND YOUR FAMILY',        body: 'Every family is different. We help you design spaces that fit your unique needs.' },
  { icon: Star,       tone: 'lime',   title: 'PLAN WITH CONFIDENCE',            body: 'Tools and guidance to help you prepare for everyday life and journeys.' },
  { icon: Search,     tone: 'cyan',   title: 'PRACTICAL SOLUTIONS FOR REAL LIFE', body: "From small changes to full transformations — we've got you covered." },
  { icon: Heart,      tone: 'pink',   title: 'SUPPORT THAT UNDERSTANDS',        body: 'Created by experts. Driven by families. Focused on what matters.' },
  { icon: Users,      tone: 'orange', title: "YOU'RE NOT ALONE",                body: 'Join a community of families creating better experiences together.' },
];

/* Four membership tiers — copy pulled verbatim from the mockup. */
const TIERS = [
  {
    code: 'FREE_ACCESS',
    name: 'Free Access',
    price: '£0',
    cadence: 'No monthly fee',
    image: '/img/access/free-access-icon.jpg',
    accent: 'purple',
    features: [
      'Essential tools & resources',
      'Selected discovery',
      'Basic journey tools',
      'N3URO Access Profile (Basic)',
      'N3URO Knowledge (Selected)',
    ],
    ctaLabel: 'JOIN FREE',
    mailto: 'mailto:hello@n3uro.world?subject=N3URO%20Access%20%E2%80%94%20Join%20Free',
  },
  {
    code: 'ACCESS_PLUS',
    name: 'Access+',
    price: '£4.99',
    cadence: '/month',
    image: '/img/access/access-plus-icon.jpg',
    accent: 'lime',
    features: [
      'Everything in Free Access',
      'Full Access Profile',
      'Digital Access Passport',
      'Advanced journey tools',
      'Save journeys & places',
      'Member pricing on sessions',
      'Parent Base member benefits',
    ],
    ctaLabel: 'CHOOSE ACCESS+',
    mailto: 'mailto:hello@n3uro.world?subject=N3URO%20Access%20%E2%80%94%20Choose%20Access%2B',
  },
  {
    code: 'FAMILY_PLUS',
    name: 'Family+',
    price: '£9.99',
    cadence: '/month',
    image: '/img/access/family-plus-icon.jpg',
    accent: 'orange',
    features: [
      'Everything in Access+',
      'Multiple family profiles',
      'Individual Access Passports',
      'Family Journey Planning',
      'Priority N3URO booking',
      'Reduced member pricing',
      'Full Home-Space toolkit',
      'Enhanced Parent Base benefits',
      'Early access to pilots & testing',
    ],
    ctaLabel: 'CHOOSE FAMILY+',
    mailto: 'mailto:hello@n3uro.world?subject=N3URO%20Access%20%E2%80%94%20Choose%20Family%2B',
    prominent: true,
  },
  {
    code: 'PREMIUM_PLUS',
    name: 'Premium+',
    price: '£19.99+',
    cadence: '/month',
    image: '/img/access/premium-plus-icon.jpg',
    accent: 'cyan',
    features: [
      'Everything in Family+',
      'Enhanced Journey Planning',
      'Priority+ N3URO booking',
      'Lowest session pricing',
      'Premium Parent Base benefits',
      'Home Solutions pathway access',
      'Premium support & more',
    ],
    ctaLabel: 'CHOOSE PREMIUM+',
    mailto: 'mailto:hello@n3uro.world?subject=N3URO%20Access%20%E2%80%94%20Choose%20Premium%2B',
  },
];

const HELP_TOPICS = [
  'Airports / air travel',
  'Train / public transport',
  'Buses / coaches / trams',
  'Leisure & hospitality',
  'Retail / shopping',
  'Education',
  'Parks / public spaces',
  'Housing / residential environments',
  'Other',
];

const ROLE_OPTIONS = [
  { value: '', label: 'I am a…' },
  { value: 'parent', label: 'Parent' },
  { value: 'carer', label: 'Carer' },
  { value: 'family_member', label: 'Family member' },
  { value: 'professional', label: 'Professional' },
  { value: 'organisation', label: 'Organisation representative' },
  { value: 'other', label: 'Other' },
];

/* ============================================================
   Neon halo — 5 icons rotating around a central glow. Pure SVG
   + CSS so no photography licensing risk and the halo scales
   cleanly across mobile → desktop.
   ============================================================ */
const HALO_ICONS = [
  { angleDeg:  270, Icon: Users,     tone: 'purple' }, // top
  { angleDeg:  342, Icon: Briefcase, tone: 'cyan' },   // top-right
  { angleDeg:   54, Icon: Heart,     tone: 'pink' },   // bottom-right
  { angleDeg:  126, Icon: HomeIcon,  tone: 'orange' }, // bottom-left
  { angleDeg:  198, Icon: MapPin,    tone: 'lime' },   // top-left
];

const AccessHalo = () => (
  <div className="access-halo" aria-hidden="true">
    <div className="access-halo-ring">
      {HALO_ICONS.map(({ angleDeg, Icon, tone }) => (
        <span
          key={`${tone}-${angleDeg}`}
          className={`access-halo-node access-halo-node--${tone}`}
          style={{ transform: `rotate(${angleDeg}deg) translateX(46%) rotate(${-angleDeg}deg)` }}
        >
          <Icon size={22} strokeWidth={1.6} />
        </span>
      ))}
      <div className="access-halo-center">
        <span className="access-halo-glow" />
      </div>
    </div>
  </div>
);

const Access = () => {
  const { member } = useMember();
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const startCheckout = async (tierCode) => {
    if (!member) { navigate('/access/join', { state: { intent: tierCode } }); return; }
    setCheckoutBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/access/billing/checkout`, {
        tier: tierCode,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, 'Could not start checkout.'));
      setCheckoutBusy(false);
    }
  };

  const handleAreaClick = (e, key, href) => {
    if (key === 'home-solutions') {
      e.preventDefault();
      setGateOpen(true);
    } else if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    region: '',
    help_topics: [],
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canSelectTopic = useMemo(
    () => form.help_topics.length < 3,
    [form.help_topics.length],
  );

  const toggleTopic = (topic) => {
    setForm((prev) => {
      const has = prev.help_topics.includes(topic);
      if (has) {
        return { ...prev, help_topics: prev.help_topics.filter((t) => t !== topic) };
      }
      if (prev.help_topics.length >= 3) return prev;
      return { ...prev, help_topics: [...prev.help_topics, topic] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.consent) {
      toast.error('Please add your name, email and consent to continue.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/access/leads`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role || null,
        region: form.region.trim() || null,
        help_topics: form.help_topics,
        source: 'access_page_hero',
      });
      setDone(true);
      track('form_submit', 'access-hero-lead');
      toast.success('Details saved. One last step to finish your Free Access Profile.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save your details. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SubpageShell currentPath="/access">
      <SEO
        title="N3URO Access"
        description="N3URO Access — neuro-inclusive family memberships that give families the tools, guidance and solutions to prepare, navigate and feel more confident at home, on journeys and out in the world."
        path="/access"
        image="https://n3uro.world/img/share-access.jpg"
      />
      <JsonLd data={[{ '@type': 'WebPage', name: 'N3URO Access', url: `${SITE}/access`, isPartOf: { '@id': `${SITE}/#website` }, publisher: PROVIDER }, { '@type': 'Service', name: 'N3URO Access', url: `${SITE}/access`, description: 'Family access and membership pathway connecting tools, discovery, journeys, home solutions, knowledge and support.', provider: PROVIDER, areaServed: 'GB' }]} />

      <div className="access-hero-image-wrap">
        <PageHeroImage src="/img/access-memberships.webp" alt="N3URO Access Memberships — a family checks in with an N3URO team member" testId="hero-image-access" />
      </div>

      {/* ============= HERO + JOIN FREE FORM ============= */}
      <section className="access-hero-band" data-testid="access-hero">
        <div className="access-hero-content">
          <div className="access-hero-copy">
            <h1 className="access-hero-title" data-testid="access-title">
              <span className="access-brand-n">N3URO </span>
              <span className="access-brand-rainbow">ACCESS</span>
            </h1>
            <p className="access-hero-lede">Neuro-inclusive family access.</p>
            <p className="access-hero-tag">
              <span className="access-brand-rainbow">Tools, guidance and solutions for everyday life.</span>
            </p>
            <p className="access-hero-body">
              N3URO Access brings everything together in one place &mdash;
              helping your family discover, prepare, plan and create better
              everyday experiences at home, on journeys and out in the world.
            </p>
            <div className="conv-cta-row" data-testid="access-hero-ctas">
              <Link to="/access/join" className="conv-btn-primary" data-cta="access-hero-create-profile" data-testid="access-cta-create-profile">
                Create your Free Access Profile
              </Link>
              <a href="#tiers" className="conv-btn-ghost" data-cta="access-hero-compare" data-testid="access-cta-compare">
                Compare memberships
              </a>
            </div>
          </div>

          <div className="access-hero-halo" data-testid="access-halo">
            <AccessHalo />
          </div>

          <aside className="access-signup" data-testid="access-signup">
            {!done ? (
              <>
                <h2 className="access-signup-title"><span className="access-brand-n">JOIN N3URO </span><span className="access-brand-rainbow">ACCESS</span></h2>
                <p className="access-signup-lede">
                  Start your FREE N3URO Access Profile. Tell us a little about your family, then set a password to finish.
                </p>
                <form className="access-signup-form" onSubmit={submit} noValidate data-track-form="access-hero-lead">
                  <div className="access-form-row">
                    <input
                      className="access-input"
                      type="text"
                      placeholder="First name"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      required
                      autoComplete="given-name"
                      data-testid="access-input-firstname"
                    />
                    <input
                      className="access-input"
                      type="text"
                      placeholder="Last name"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      autoComplete="family-name"
                      data-testid="access-input-lastname"
                    />
                  </div>
                  <input
                    className="access-input"
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    autoComplete="email"
                    data-testid="access-input-email"
                  />
                  <select
                    className="access-input access-select"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    data-testid="access-input-role"
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <input
                    className="access-input"
                    type="text"
                    placeholder="Region / nearest city"
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                    autoComplete="address-level2"
                    data-testid="access-input-region"
                  />

                  <fieldset className="access-topics">
                    <legend className="access-topics-legend">
                      What would you most like N3URO Access to help with? (Select up to 3)
                    </legend>
                    <div className="access-topics-grid">
                      {HELP_TOPICS.map((topic) => {
                        const checked = form.help_topics.includes(topic);
                        const disabled = !checked && !canSelectTopic;
                        return (
                          <label
                            key={topic}
                            className={`access-topic ${checked ? 'is-checked' : ''} ${disabled ? 'is-disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleTopic(topic)}
                              data-testid={`access-topic-${topic.toLowerCase().replace(/\s+|\//g, '-')}`}
                            />
                            <span>{topic}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  <label className="access-consent">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      required
                      data-testid="access-input-consent"
                    />
                    <span>
                      I agree to be contacted about N3URO Access and future
                      updates. (You can unsubscribe at any time.)
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="access-signup-cta"
                    disabled={submitting}
                    data-testid="access-signup-submit"
                  >
                    {submitting ? 'JOINING…' : (<>JOIN FREE ACCESS <ArrowRight size={16} /></>)}
                  </button>
                  <p className="access-signup-fineprint">
                    Free for parents and carers. No card required.
                  </p>
                </form>
              </>
            ) : (
              <div className="access-signup-done" data-testid="access-signup-done">
                <div className="access-signup-check"><Check size={28} strokeWidth={2.5} /></div>
                <h2 className="access-signup-title">Almost there.</h2>
                <p className="access-signup-lede">
                  Thank you &mdash; we&rsquo;ve saved your details. Set a password to
                  finish your Free Access Profile and open your account.
                </p>
                <Link
                  to="/access/join"
                  state={{ prefill: { first_name: form.first_name.trim(), last_name: form.last_name.trim(), email: form.email.trim().toLowerCase() } }}
                  className="conv-btn-primary"
                  data-cta="access-hero-finish-profile"
                  data-testid="access-signup-finish"
                >
                  Finish your Free Access Profile
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* ============= 5 PRIMARY AREA CARDS ============= */}
      <section className="access-areas" data-testid="access-areas">
        <div className="access-areas-head">
          <h2 className="access-areas-title">
            EXPLORE THE <span className="access-brand-rainbow">N3URO ACCESS</span> ECOSYSTEM
          </h2>
          <p className="access-areas-lede">
            Five connected areas. One purpose &mdash; to support your
            family&rsquo;s access, confidence and wellbeing.
          </p>
        </div>
        <div className="access-areas-grid">
          {PRIMARY_AREAS.map(({ key, image, accent, label, body, href }) => (
            <a
              key={key}
              href={href}
              className={`access-area access-area--${accent}`}
              data-testid={`access-area-${key}`}
              onClick={(e) => handleAreaClick(e, key, href)}
              aria-label={`${label}: ${body}`}
            >
              <span className="access-area-image" aria-hidden="true">
                <img src={image} alt="" loading="lazy" />
              </span>
              <div className="access-area-body-wrap">
                <h3 className="access-area-label">{label}</h3>
                <p className="access-area-body">{body}</p>
                <span className="access-area-arrow" aria-hidden="true">
                  <ArrowRight size={18} strokeWidth={2} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ============= VALUE-PROP BAND ============= */}
      <section className="access-valueprops" data-testid="access-valueprops">
        <h2 className="access-valueprops-title">
          What joining <span className="access-brand-rainbow">N3URO ACCESS</span> means for your family
        </h2>
        <div className="access-valueprops-grid">
          {VALUE_PROPS.map(({ icon: Icon, tone, body }) => (
            <div key={`${tone}-${String(body).slice(0, 24)}`} className={`access-value access-value--${tone}`}>
              <span className="access-value-icon" aria-hidden="true">
                <Icon size={22} strokeWidth={1.75} />
              </span>
              <p className="access-value-body">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============= 4 MEMBERSHIP TIERS ============= */}
      <section id="tiers" className="access-tiers-section" data-testid="access-tiers">
        <div className="access-tiers-head">
          <h2 className="access-tiers-title">Choose the level of access that works for you</h2>
          <p className="access-tiers-lede">
            Start free, then unlock more of the N3URO WORLD® ecosystem as your family needs it.
          </p>
        </div>
        <div className="access-tier-grid">
          {TIERS.map(({ code, name, price, cadence, image, accent, features, ctaLabel, mailto, prominent }) => {
            const isCurrent = member && member.tier === code;
            const isFree = code === 'FREE_ACCESS';
            const label = isCurrent
              ? 'YOUR CURRENT TIER'
              : (member && !isFree)
                ? ctaLabel
                : (!member && !isFree)
                  ? `${ctaLabel}`
                  : ctaLabel;
            const commonProps = {
              className: `access-tier-cta access-tier-cta--${accent}`,
              'data-testid': `tier-cta-${code.toLowerCase()}`,
            };
            let ctaEl;
            if (isCurrent) {
              ctaEl = <Link to="/access/account" {...commonProps}>{label}</Link>;
            } else if (isFree) {
              // Free tier — send to /access/join for a new member,
              // /access/account for anyone already signed in.
              ctaEl = (
                <Link to={member ? '/access/account' : '/access/join'} {...commonProps}>
                  {ctaLabel}
                </Link>
              );
            } else if (member) {
              // Signed-in paid tier → start Stripe subscription checkout.
              ctaEl = (
                <button
                  type="button"
                  onClick={() => startCheckout(code)}
                  disabled={checkoutBusy}
                  {...commonProps}
                >
                  {checkoutBusy ? 'OPENING…' : ctaLabel}
                </button>
              );
            } else {
              // Signed-out paid tier → prompt to join first, checkout starts from account page.
              ctaEl = (
                <Link
                  to="/access/join"
                  state={{ intent: code }}
                  {...commonProps}
                >
                  {ctaLabel}
                </Link>
              );
            }
            return (
              <article
                key={code}
                className={`access-tier access-tier--${accent} ${prominent ? 'is-prominent' : ''} ${isCurrent ? 'is-current' : ''}`}
                data-testid={`tier-${code.toLowerCase()}`}
              >
                <header className="access-tier-header">
                  <span className="access-tier-icon" aria-hidden="true">
                    <img src={image} alt="" loading="lazy" />
                  </span>
                  <h3 className="access-tier-name">{name.toUpperCase()}</h3>
                  <div className="access-tier-price">
                    <span className="access-tier-price-value">{price}</span>
                    <span className="access-tier-price-cadence">{cadence}</span>
                  </div>
                </header>
                <ul className="access-tier-features">
                  {features.map((f) => (
                    <li key={f}>
                      <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {ctaEl}
              </article>
            );
          })}
        </div>
        <p className="access-tiers-note">
          <Lock size={13} strokeWidth={2} aria-hidden="true" />
          Cancel or change your plan at any time.
        </p>
      </section>

      {/* ============= CLOSING VALUE-PROPS (5 titled) ============= */}
      <section className="access-closing-props" data-testid="access-closing-props">
        <div className="access-closing-grid">
          {CLOSING_PROPS.map(({ icon: Icon, tone, title, body }) => (
            <div key={title} className={`access-cprop access-cprop--${tone}`}>
              <span className="access-cprop-icon" aria-hidden="true">
                <Icon size={26} strokeWidth={1.75} />
              </span>
              <div className="access-cprop-copy">
                <h3 className="access-cprop-title">{title}</h3>
                <p className="access-cprop-body">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============= CLOSING BAND ============= */}
      <section className="access-closing" data-testid="access-closing">
        <div className="access-closing-row">
          <span className="access-closing-item"><strong>EXPLORE.</strong></span>
          <span className="access-closing-item access-closing-item--pink"><strong>REGULATE.</strong></span>
          <span className="access-closing-item access-closing-item--cyan"><strong>CONNECT.</strong></span>
        </div>
        <p className="access-closing-brand">
          <Link to="/">N3URO.<span className="access-closing-tld">world</span></Link>
        </p>
      </section>
      <HomeSolutionsGate open={gateOpen} onClose={() => setGateOpen(false)} />
    </SubpageShell>
  );
};

export default Access;
